'use client';

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import darkTheme from '@/app/theme/dark-theme';
import lightTheme1 from '@/app/theme/light-theme';
import highContrastTheme from '@/app/theme/high-contrast-theme';
import { checkSystemPrefersDark, safeGetItem, safeSetItem } from '@/app/lib/compatibility/browser-apis';

// Define theme types
export type ThemeMode = 'dark' | 'light' | 'high-contrast';
export type TextSize = 'small' | 'medium' | 'large';

const availableThemeModes: ThemeMode[] = ['dark', 'light', 'high-contrast'];

const normalizeThemeMode = (value: string | null): ThemeMode | null => {
  if (!value) return null;
  if (value === 'light-1' || value === 'light-2' || value === 'light-3') return 'light';
  if (availableThemeModes.includes(value as ThemeMode)) return value as ThemeMode;
  return null;
};

const getInitialThemeMode = (): ThemeMode => {
  if (typeof window === 'undefined') return 'dark';
  const storedTheme = normalizeThemeMode(safeGetItem('theme'));
  if (storedTheme) return storedTheme;
  return checkSystemPrefersDark() ? 'dark' : 'light';
};

const getInitialTextSize = (): TextSize => {
  if (typeof window === 'undefined') return 'medium';
  const storedTextSize = safeGetItem('textSize') as TextSize | null;
  if (storedTextSize && ['small', 'medium', 'large'].includes(storedTextSize)) {
    return storedTextSize;
  }
  return 'medium';
};

// Theme context types
interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  textSize: TextSize;
  setTextSize: (size: TextSize) => void;
}

// Create the context with default values
const ThemeContext = createContext<ThemeContextType>({
  themeMode: 'dark',
  setThemeMode: () => {},
  textSize: 'medium',
  setTextSize: () => {},
});

// Hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Theme provider component
export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialize theme from localStorage or system preference
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialThemeMode);
  const [textSize, setTextSize] = useState<TextSize>(getInitialTextSize);
  const [mounted, setMounted] = useState(false);

  // Get the current theme object based on the mode
  const getTheme = (mode: ThemeMode) => {
    switch (mode) {
      case 'light':
        return lightTheme1;
      case 'high-contrast':
        return highContrastTheme;
      case 'dark':
      default:
        return darkTheme;
    }
  };

  // Using browser compatibility utilities imported at the top of the file

  // Effect to mark client mount
  useEffect(() => {
    if (typeof window !== 'undefined') setMounted(true);
  }, []);

  // Effect to update localStorage when theme changes
  useEffect(() => {
    if (mounted) {
      safeSetItem('theme', themeMode);

      try {
        // Update CSS variables for non-MUI ui
        document.documentElement.setAttribute('data-theme', themeMode);

        // Update body class for global styling
        document.body.classList.remove('theme-dark', 'theme-light', 'theme-high-contrast');
        document.body.classList.add(`theme-${themeMode}`);
      } catch (error) {
        console.warn('Error applying theme changes:', error);
      }
    }
  }, [themeMode, mounted]);

  // Effect to update localStorage and apply text size changes
  useEffect(() => {
    if (mounted) {
      safeSetItem('textSize', textSize);

      try {
        // Update data attribute for text size
        document.documentElement.setAttribute('data-text-size', textSize);

        // Update body class for text size styling
        document.body.classList.remove('text-small', 'text-medium', 'text-large');
        document.body.classList.add(`text-${textSize}`);

        // Apply font size scaling based on text size
        const htmlElement = document.documentElement;
        switch (textSize) {
          case 'small':
            htmlElement.style.fontSize = '14px';
            break;
          case 'medium':
            htmlElement.style.fontSize = '16px';
            break;
          case 'large':
            htmlElement.style.fontSize = '18px';
            break;
        }
      } catch (error) {
        console.warn('Error applying text size changes:', error);
      }
    }
  }, [textSize, mounted]);

  // Function to change the theme
  const handleThemeChange = (mode: ThemeMode) => {
    setThemeMode(mode);
  };

  // Function to change the text size
  const handleTextSizeChange = (size: TextSize) => {
    setTextSize(size);
  };

  // Context value
  const contextValue = {
    themeMode,
    setThemeMode: handleThemeChange,
    textSize,
    setTextSize: handleTextSizeChange,
  };

  // Always provide a theme to prevent "Cannot read properties of undefined" errors
  // even when not mounted, but use a hidden div to prevent content flash
  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={getTheme(themeMode)}>
        {!mounted ? (
          <div style={{ height: 0, visibility: 'hidden' }}>{children}</div>
        ) : (
          children
        )}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
